import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'

/**
 * Returns whether the current browser tab is visible.
 * Defaults to true if the Page Visibility API is unavailable (SSR, old browsers, sandboxed iframes).
 */
export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    try {
      return typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
    } catch {
      return true
    }
  })

  useEffect(() => {
    try {
      if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
        return
      }

      const handleVisibilityChange = () => {
        try {
          setIsVisible(document.visibilityState === 'visible')
        } catch (err) {
          Sentry.captureException(err, { tags: { hook: 'usePageVisibility' } })
          setIsVisible(true)
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { hook: 'usePageVisibility' } })
    }
  }, [])

  return isVisible
}
